import { motion } from 'framer-motion';
import { ArrowUpRight, Linkedin, Twitter, Github } from 'lucide-react';
import { ShopifyBadge, MicrosoftBadge } from './PartnerBadges';

const serviceLinks = [
  { name: 'Shopify Development',       href: '/services/shopify' },
  { name: 'Marketing & Creative',      href: '/services/marketing' },
  { name: 'IT & Cloud Solutions',      href: '/services/it-solutions' },
  { name: 'Cybersecurity & Networking',href: '/services/networking' },
  { name: 'Software & Mobile Apps',    href: '/services/software' },
  { name: 'Microsoft 365 & Azure',     href: '/services/it-solutions' },
  { name: 'Integrations & Partnerships',href: '/services/integrations' },
];

const companyLinks = [
  { name: 'About Us',   href: '#about' },
  { name: 'Services',   href: '#services' },
  { name: 'Industries', href: '#industries' },
  { name: 'Contact',    href: '#contact' },
];

export const Footer = () => {
  const scrollToSection = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#0F172A] border-t border-[#1E40AF]" data-testid="footer">
      {/* Top divider */}
      <div
        className="h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.5), transparent)' }}
      />

      <div className="container-custom pt-24 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-14">

          {/* Brand */}
          <div className="lg:col-span-1">
            <motion.button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              whileHover={{ opacity: 0.8 }}
              className="mb-5 flex items-center"
            >
              <span className="font-display text-xl font-bold text-white">Smart</span>
              <span className="font-display text-xl font-bold text-[#3B82F6]">Solutions</span>
            </motion.button>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
              Technology consulting and development company delivering custom software,
              AI solutions, and enterprise-grade platforms.
            </p>

            {/* Partner badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              <ShopifyBadge variant="dark-compact" />
              <MicrosoftBadge variant="dark-compact" />
            </div>

            {/* Social */}
            <div className="flex gap-3">
              {[
                { Icon: Linkedin, testId: 'social-linkedin' },
                { Icon: Twitter,  testId: 'social-twitter' },
                { Icon: Github,   testId: 'social-github' },
              ].map(({ Icon, testId }) => (
                <motion.a
                  key={testId}
                  href="#"
                  whileHover={{ y: -2 }}
                  className="w-8 h-8 border border-[#1E40AF] flex items-center justify-center text-[#94A3B8] hover:border-[#3B82F6]/40 hover:text-[#3B82F6] transition-all duration-300"
                  data-testid={testId}
                >
                  <Icon size={14} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-mono text-[0.6rem] tracking-[0.22em] text-[#3B82F6] uppercase mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((service) => (
                <li key={service.name}>
                  <a
                    href={service.href}
                    className="group text-[#94A3B8] hover:text-white text-sm transition-colors flex items-center gap-1"
                  >
                    {service.name}
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-mono text-[0.6rem] tracking-[0.22em] text-[#3B82F6] uppercase mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.name}>
                  <button
                    onClick={() => scrollToSection(item.href)}
                    className="group text-[#94A3B8] hover:text-white text-sm transition-colors flex items-center gap-1"
                  >
                    {item.name}
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-mono text-[0.6rem] tracking-[0.22em] text-[#3B82F6] uppercase mb-5">
              Contact
            </h4>
            <ul className="space-y-4 text-sm">
              {[
                { label: 'Email',    value: 'hello@smartsolution.ai' },
                { label: 'Phone',    value: '+965 6596 6786' },
                { label: 'Location', value: 'Kuwait City, Kuwait' },
              ].map(({ label, value }) => (
                <li key={label}>
                  <span className="font-mono text-[0.55rem] tracking-widest text-[#94A3B8]/70 uppercase block mb-0.5">
                    {label}
                  </span>
                  <span className="text-[#94A3B8]">{value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-[#1E40AF] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[0.58rem] tracking-widest text-[#5B7299] uppercase">
            © {new Date().getFullYear()} Smart Solution · All Rights Reserved
          </p>
          <div className="flex gap-6">
            {['Privacy Policy', 'Terms of Service'].map((label) => (
              <button
                key={label}
                className="font-mono text-[0.58rem] tracking-widest text-[#5B7299] hover:text-[#94A3B8] uppercase transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
